import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listEvents, listMembers, approveMember, denyMember, suspendMember, removeMember, listAudit } from '@/lib/events-api';
import { ArrowLeft, CheckCircle, XCircle, PauseCircle, MinusCircle, Shield, ChevronDown, ChevronUp } from 'lucide-react';

export default function HostGuestsPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const passedEventId = sp.get('event_id');

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(passedEventId || '');
  const [members, setMembers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);

  const loadEvents = useCallback(async () => {
    try {
      const body = await listEvents();
      setEvents(body.hosted || []);
      if (!eventId && body.hosted?.[0]) {
        const best = body.hosted.sort((a, b) => (b.pending_approvals_count || 0) - (a.pending_approvals_count || 0))[0];
        setEventId(best.id);
      }
    } catch {}
  }, [eventId]);

  const loadMembers = useCallback(async () => {
    if (!eventId) return;
    setLoading(true); setError(null);
    try {
      const [m, a] = await Promise.all([listMembers(eventId), listAudit(eventId, 20)]);
      setMembers(m.members || []);
      setAudit(a.audit || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { if (ready) loadEvents(); }, [ready, loadEvents]);
  useEffect(() => { if (ready && eventId) loadMembers(); }, [ready, eventId, loadMembers]);

  async function doAction(memberId, action) {
    setActionBusy(memberId); setError(null);
    try {
      const fns = { approve: approveMember, deny: denyMember, suspend: suspendMember, remove: removeMember };
      await fns[action](eventId, memberId);
      await loadMembers();
    } catch (e) { setError(e.message); }
    finally { setActionBusy(null); }
  }

  const pending = members.filter(m => m.status === 'HOST_PENDING');
  const active = members.filter(m => !['HOST_PENDING', 'REMOVED'].includes(m.status));
  const removed = members.filter(m => m.status === 'REMOVED');

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="host-guests-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="guests-back-btn">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-4">Manage Guests</h1>

      {events.length > 1 && (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-3 mb-4">
          <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">Switch event</p>
          <div className="flex flex-col gap-1">
            {events.map(e => (
              <button key={e.id} onClick={() => setEventId(e.id)}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${e.id === eventId ? 'bg-[#EFF5FF] text-[#146BFF] font-bold' : 'bg-[#F3F5F8] hover:bg-[#E3E8EF]'}`}>
                <span className="truncate">{e.name}</span>
                {(e.pending_approvals_count || 0) > 0 && <span className="text-xs font-bold text-[#B86500]">{e.pending_approvals_count} pending</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="flex flex-col gap-4">
          {error && <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]">{error}</div>}

          {pending.length > 0 && (
            <div>
              <p className="text-[10px] font-extrabold text-[#B86500] tracking-widest mb-2 uppercase">Pending approval ({pending.length})</p>
              {pending.map(m => (
                <div key={m.id} className="bg-[#FFF6E5] border border-[#B86500]/20 rounded-xl p-3 mb-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-[#0B1B46]">{m.user_first_name} {m.user_last_name}</p>
                    <p className="text-xs text-[#5B6472]">{m.user_email}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => doAction(m.id, 'approve')} disabled={actionBusy === m.id}
                      className="h-8 px-3 bg-[#146BFF] text-white text-xs font-bold rounded-lg hover:bg-[#0F55CC] disabled:opacity-50">Approve</button>
                    <button onClick={() => doAction(m.id, 'deny')} disabled={actionBusy === m.id}
                      className="h-8 px-3 bg-white border border-[#E3E8EF] text-xs font-bold rounded-lg hover:bg-[#F3F5F8] disabled:opacity-50">Deny</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {active.length > 0 && (
            <div>
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-2 uppercase">All attendees ({active.length})</p>
              {active.map(m => (
                <div key={m.id} className="bg-white border border-[#E3E8EF] rounded-xl p-3 mb-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-[#0B1B46]">{m.user_first_name} {m.user_last_name}</p>
                    <div className="flex gap-1 mt-1">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${m.status === 'GEO_VERIFIED' ? 'bg-[#E4F4EC] text-[#1F8A55]' : m.status === 'HOST_APPROVED' ? 'bg-[#EFF5FF] text-[#146BFF]' : 'bg-[#F3F5F8] text-[#5B6472]'}`}>{m.status}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${m.canUpload ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#FBEEDA] text-[#B86500]'}`}>{m.canUpload ? 'Can upload' : 'Locked'}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {m.status !== 'SUSPENDED' && m.role !== 'HOST' && (
                      <button onClick={() => doAction(m.id, 'suspend')} disabled={actionBusy === m.id} title="Suspend"
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E3E8EF] hover:bg-[#F3F5F8] disabled:opacity-50">
                        <PauseCircle className="w-4 h-4 text-[#B86500]" />
                      </button>
                    )}
                    {m.role !== 'HOST' && (
                      <button onClick={() => doAction(m.id, 'remove')} disabled={actionBusy === m.id} title="Remove"
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E3E8EF] hover:bg-[#F3F5F8] disabled:opacity-50">
                        <MinusCircle className="w-4 h-4 text-[#C93434]" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {removed.length > 0 && (
            <div>
              <p className="text-[10px] font-extrabold text-[#5B6472] tracking-widest mb-2 uppercase">Removed ({removed.length})</p>
              {removed.map(m => (
                <div key={m.id} className="bg-[#F3F5F8] border border-[#E3E8EF] rounded-xl p-3 mb-2 opacity-60">
                  <p className="text-sm text-[#5B6472]">{m.user_first_name} {m.user_last_name} — Removed</p>
                </div>
              ))}
            </div>
          )}

          {/* Audit log */}
          <div>
            <button onClick={() => setShowAudit(s => !s)} className="flex items-center gap-2 text-sm font-bold text-[#0B1B46] mb-2">
              {showAudit ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Audit Log
            </button>
            {showAudit && (
              <div className="bg-white border border-[#E3E8EF] rounded-xl p-3">
                {audit.length === 0 ? <p className="text-sm text-[#5B6472]">No audit entries yet.</p> : (
                  audit.map(a => (
                    <div key={a.id} className="flex items-start gap-2 py-2 border-b border-[#E3E8EF] last:border-0">
                      <Shield className="w-3.5 h-3.5 text-[#146BFF] mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-[#0B1B46]"><span className="font-bold">{a.actor_name}</span> {a.action.toLowerCase().replace(/_/g, ' ')} {a.target_user_name ? <span className="font-bold">{a.target_user_name}</span> : null}</p>
                        {a.reason && <p className="text-xs text-[#5B6472] italic">"{a.reason}"</p>}
                        <p className="text-[10px] text-[#5B6472]">{new Date(a.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
